import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { ThemeService } from '@shared/services/theme.service';
import { UtmInterceptorService } from '@shared/services/utm-interceptor.service';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { LanguageService } from '@shared/shared/services/language-service';
import { SiteConfigService } from '@shared/shared/services/site-config.service';
import { UserLogout } from '@shared/store/user';
import { PostMessageService } from 'core/services/post-message.service';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Injectable()
export class AppInitializerService implements OnDestroy {
  private messageSubscription: Subscription;

  constructor(
    private siteSettingsService: SiteSettingsService,
    private languageService: LanguageService,
    private siteConfigService: SiteConfigService,
    private postMessageService: PostMessageService,
    private themeService: ThemeService,
    private store: Store,
    private utmInterceptorService: UtmInterceptorService
  ) {}

  public ngOnDestroy() {
    this.messageSubscription.unsubscribe();
  }

  // private updateMetaTag(value: number) {
  //   const metaTag = document.querySelector('meta[http-equiv="csp-nonce"]');

  //   if (metaTag) {
  //     metaTag.setAttribute('content', getCspTagData(value));
  //   }
  // }

  public async initializeApplication(): Promise<void> {
    // Initialize utm_source parameter tracking
    this.utmInterceptorService.initialize();

    if (this.canSkipSelectorPageWithSiteId()) {
      const urlParams = new URLSearchParams(window.location.search);
      const siteId = `${urlParams.get('site_id')}`;
      const token = `${urlParams.get('auth_token')}`;

      if (siteId !== 'null' && token !== 'null') {
        this.store.dispatch(new UserLogout());
      }
    }

    const siteSettings = await this.getSiteSettings();
    const clientName = siteSettings.fe_configuration.path;
    this.messageSubscription = this.setCustomVariablesOnMessage();

    this.setSiteLanguage(siteSettings);
    await this.setSiteTheme(siteSettings);

    await this.siteConfigService.getAllComponentData(clientName);

    // if (this.siteConfigService.getComponentData('CSP').default.enabled) {
    //   this.updateMetaTag(cspNonceValue);
    // }
  }

  public canSkipSelectorPageWithSiteId() {
    const allowedSites = ['mastercard', 'hsbc'];
    let canSkip = false;

    allowedSites.forEach((site) => {
      if (canSkip) {
        return;
      }
      canSkip = window.location.origin.includes(site);
    });

    return canSkip;
  }

  private async setSiteTheme(siteSettings: ISiteSettings) {
    this.themeService.loadStyle(siteSettings.fe_configuration.path);

    const siteCss = await this.siteSettingsService.getSiteCss();
    this.themeService.setCssTheme(siteCss);

    this.themeService.setUpTheme(siteSettings);

    const siteAdminCss = await this.siteSettingsService.getDesignAdminSiteCss();
    this.themeService.setCssTheme(siteAdminCss);
  }

  private setSiteLanguage(siteSettings: ISiteSettings) {
    let siteLanguage = 'en-gb';

    if (!siteSettings.language) {
      console.warn('No language detected falling back to english');
    } else {
      siteLanguage = siteSettings.language;
    }

    this.languageService.initLanguageService(siteLanguage);
  }

  public getSiteSettings(): Promise<ISiteSettings> {
    return new Promise((resolve) => {
      this.siteSettingsService
        .fetchSiteSettings()
        .pipe(
          filter((siteSettings: ISiteSettings) => this.siteSettingsService.settingsRetrieved && !!siteSettings.id),
          take(1)
        )
        .subscribe((siteSettings: ISiteSettings) => {
          resolve(siteSettings);
        });
    });
  }

  private setCustomVariablesOnMessage(): Subscription {
    return this.postMessageService.message$.subscribe((variables) =>
      this.themeService.appendCustomVariables(variables)
    );
  }
}
